import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Building2, ClipboardList, Copy, FilePlus2, FileText, Home, PackagePlus, Search, ShieldCheck, Store, UserCheck, UserCog, Users } from 'lucide-react';
import { API_URL } from '../config/api';
import { AUTH_SESSION_CHANGED_EVENT, clearAuthSession, pushImpersonationSession, setAuthSession } from '../utils/auth';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const USER_ROLE_ORDER = {
  superadmin: 1,
  admin: 2,
  whitelabel_admin: 3,
  employee: 4,
  dealer: 5,
  customer: 6,
};

const getCreatedTime = (item) => {
  const time = new Date(item?.created_at || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortUsersByRole = (items = []) => [...items].sort((a, b) => {
  const roleDiff = (USER_ROLE_ORDER[a.role] || 99) - (USER_ROLE_ORDER[b.role] || 99);
  if (roleDiff !== 0) return roleDiff;
  const timeDiff = getCreatedTime(b) - getCreatedTime(a);
  if (timeDiff !== 0) return timeDiff;
  return String(a.name || '').localeCompare(String(b.name || ''));
});

const USER_MANAGEMENT_SUBMENU_TABS = {
  'users-grouped': 'grouped',
  'users-admins': 'admin',
  'users-white-label': 'whitelabel_admin',
  'users-employees': 'employee',
  'users-dealers': 'dealer',
  'users-customers': 'customer',
  'users-office-location-map': 'warehouse-map',
  'users-create-office-location': 'warehouse-create',
  'users-create-user': 'create',
  'users-master-search': 'master-search',
};

const USER_MANAGEMENT_REPORT_SUBMENU_TABS = {
  'users-report-summary': 'summary',
  'users-report-all': 'all',
  'users-report-admin': 'admin',
  'users-report-whitelabel': 'whitelabel_admin',
  'users-report-employee': 'employee',
  'users-report-dealer': 'dealer',
  'users-report-customer': 'customer',
  'users-report-active': 'active',
  'users-report-inactive': 'inactive',
  'users-report-by-admin': 'by_admin',
};

export default function UserManagement({ userView = '' }) {
  const user = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoAccount, setDemoAccount] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseAssignments, setWarehouseAssignments] = useState([]);
  const [mappingEmployeeId, setMappingEmployeeId] = useState('');
  const [mappingWarehouseIds, setMappingWarehouseIds] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [showWarehouseCreate, setShowWarehouseCreate] = useState(false);
  const [newWarehouseName, setNewWarehouseName] = useState('');
  const [newWarehouseAddress, setNewWarehouseAddress] = useState('');
  const [newWarehouseNote, setNewWarehouseNote] = useState('');
  const [warehouseCreateLoading, setWarehouseCreateLoading] = useState(false);
  const [demoName, setDemoName] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('employee');
  const [activeTab, setActiveTab] = useState(user?.role === 'superadmin' ? 'grouped' : 'all');
  const [selectedUserTileId, setSelectedUserTileId] = useState('');
  const [userListSearch, setUserListSearch] = useState('');
  const [userListPage, setUserListPage] = useState(1);
  const [reportPreview, setReportPreview] = useState(null);
  const [temporaryLoginResult, setTemporaryLoginResult] = useState(null);
  const [temporaryPasswordCopied, setTemporaryPasswordCopied] = useState(false);
  const [masterSearchTerm, setMasterSearchTerm] = useState('');
  const [masterSearchLoading, setMasterSearchLoading] = useState(false);
  const [masterSearchResult, setMasterSearchResult] = useState(null);
  const [masterSearchError, setMasterSearchError] = useState('');

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoadError('');
      const res = await axios.get(`${API_URL}/api/users`, { headers: getHeaders() });
      setUsers(res.data);
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) {
        clearAuthSession();
        navigate('/login');
        return;
      }
      setLoadError(err.response?.data?.message || 'Could not load users. Please refresh and try again.');
      console.log(err);
    }
  }, [getHeaders, navigate]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const inp = { width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#374151' };
  const adminRoles = ['admin', 'whitelabel_admin'];
  const teamRoles = ['employee', 'dealer', 'customer'];
  const listTabKeys = ['all', 'admin', 'whitelabel_admin', 'employee', 'dealer', 'customer'];
  const isSuperAdmin = user?.role === 'superadmin';
  const isWorkspaceAdmin = ['admin', 'whitelabel_admin'].includes(user?.role);
  const isEmployee = user?.role === 'employee';
  const canMapWarehouses = isSuperAdmin || isWorkspaceAdmin;
  const defaultCreateRole = isSuperAdmin ? 'admin' : isEmployee ? 'dealer' : 'employee';
  const roleOptions = isSuperAdmin
    ? ['admin', 'whitelabel_admin']
    : isEmployee
      ? ['dealer', 'customer']
      : ['employee', 'dealer', 'customer'];
  const roleColors = {
    superadmin: { bg: '#fef3c7', color: '#92400e' },
    admin: { bg: '#dbeafe', color: '#1d4ed8' },
    whitelabel_admin: { bg: '#cffafe', color: '#0e7490' },
    employee: { bg: '#d1fae5', color: '#065f46' },
    dealer: { bg: '#ede9fe', color: '#6d28d9' },
    customer: { bg: '#e9f7d9', color: '#3f7d16' },
  };
  const actionColumnCellStyle = { padding: '8px 12px', width: '430px', minWidth: '430px', verticalAlign: 'middle' };
  const actionHeaderStyle = { width: '430px', minWidth: '430px' };
  const actionOrder = ['portal', 'enter', 'temporary', 'reset2fa', 'warehouse', 'edit', 'photo', 'clean', 'delete', 'status'];
  const actionBarStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '6px',
    maxWidth: '406px'
  };
  const actionButtonBase = {
    width: 'auto',
    minWidth: '92px',
    minHeight: '28px',
    border: 'none',
    padding: '5px 8px',
    borderRadius: '7px',
    cursor: 'pointer',
    fontSize: '11px',
    lineHeight: 1.15,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box'
  };
  const actionTones = {
    portal: { background: '#fef3c7', color: '#92400e' },
    enter: { background: '#eef2ff', color: '#3730a3' },
    temporary: { background: '#fff7ed', color: '#c2410c' },
    reset2fa: { background: '#f3e8ff', color: '#7e22ce' },
    warehouse: { background: '#ccfbf1', color: '#0f766e' },
    edit: { background: '#dbeafe', color: '#1d4ed8' },
    photo: { background: '#d1fae5', color: '#065f46' },
    clean: { background: '#fef9c3', color: '#854d0e' },
    delete: { background: '#fee2e2', color: '#b91c1c' },
    disable: { background: '#fee2e2', color: '#dc2626' },
    enable: { background: '#d1fae5', color: '#065f46' }
  };
  const orderedUsers = useMemo(() => sortUsersByRole(users), [users]);
  const createRoleDescriptions = {
    admin: 'Company Admin — Standard CRM owner with full company workspace access.',
    whitelabel_admin: 'White Label Admin — Brand owner with custom brand, portal domain, and mobile app workspace.',
    employee: 'Employee — Team member who can feed CRM, customer, and sales data.',
    dealer: 'Dealer — Partner account that sees assigned customers, payments, and renewals.',
    customer: 'Customer — End-user GPS tracking & vehicle dashboard account.',
  };

  const formatRole = (r = '') => {
    if (r === 'whitelabel_admin') return 'White Label Admin';
    if (r === 'admin') return 'Company Admin';
    return r.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
  };
  const isDemoAdminAccount = (target) => Number(target?.is_demo_admin || 0) === 1
    || Boolean(target?.demo_expires_at)
    || Boolean(target?.demo_created_by)
    || String(target?.username || '').toLowerCase().startsWith('demo_')
    || String(target?.email || '').toLowerCase().endsWith('@demo.local')
    || String(target?.mobile || '').toUpperCase().startsWith('DEMO');
  const isExpiredDemoAccount = (target) => isDemoAdminAccount(target)
    && target.demo_expires_at
    && new Date(target.demo_expires_at).getTime() <= Date.now();
  const isPortalAdminAccount = (target) => adminRoles.includes(target?.role);
  const toAbsolutePortalUrl = (value) => {
    const url = String(value || '').trim();
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!origin) return url;
    return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
  };
  const getAssignedPortalUrl = (target) => {
    if (!target || !isPortalAdminAccount(target)) return '';
    if (isDemoAdminAccount(target)) return toAbsolutePortalUrl(target.assigned_portal_url || '/');
    return toAbsolutePortalUrl(target.assigned_portal_url || target.portal_url || `/?wl=${target.id}`);
  };
  const openAssignedPortal = (target) => {
    const portalUrl = getAssignedPortalUrl(target);
    if (portalUrl) window.open(portalUrl, '_blank', 'noreferrer');
  };
  const roleCount = (roleName) => users.filter(u => u.role === roleName).length;
  const getUsersByAdmin = (adminId) => orderedUsers.filter(u => Number(u.created_by) === Number(adminId));
  const getAdminTreeUsers = (adminId) => {
    const directUsers = getUsersByAdmin(adminId);
    const employeeIds = new Set(directUsers.filter(u => u.role === 'employee').map(u => Number(u.id)));
    const employeeChildren = orderedUsers.filter(u => employeeIds.has(Number(u.created_by)) && ['dealer', 'customer'].includes(u.role));
    const seen = new Set();
    return [...directUsers, ...employeeChildren].filter((item) => {
      if (seen.has(Number(item.id))) return false;
      seen.add(Number(item.id));
      return true;
    });
  };
  const getCreatedByName = (u) => {
    const creator = orderedUsers.find(item => Number(item.id) === Number(u.created_by));
    const creatorRole = u.created_by_role || creator?.role;
    const creatorName = u.created_by_name || creator?.name || '-';
    if (adminRoles.includes(u.role) && u.created_by) return 'BizTrack';
    if (creatorRole === 'superadmin' && u.created_by) return 'BizTrack';
    return creatorName;
  };
  const getOfficeLocation = (target) => {
    if (!target || !canMapWarehouses) return '-';
    let officeOwner = target.role === 'employee' ? target : orderedUsers.find(item => Number(item.id) === Number(target.created_by));
    let depth = 0;
    while (depth < 3 && officeOwner && officeOwner.role !== 'employee') {
      const nextCreatedBy = officeOwner.created_by;
      officeOwner = orderedUsers.find(item => Number(item.id) === Number(nextCreatedBy));
      depth += 1;
    }
    if (!officeOwner || officeOwner.role !== 'employee') return '-';

    const locations = warehouseAssignments
      .filter((assignment) => Number(assignment.employee_id) === Number(officeOwner.id))
      .map((assignment) => {
        const warehouse = warehouses.find((item) => Number(item.id) === Number(assignment.warehouse_id));
        return assignment.warehouse_name || warehouse?.name || '';
      })
      .filter(Boolean);
    return locations.length ? [...new Set(locations)].join(', ') : '-';
  };
  const getUsersForTab = (tabKey) => {
    if (tabKey === 'all') return orderedUsers;
    if (tabKey === 'grouped') return isSuperAdmin ? orderedUsers.filter(u => adminRoles.includes(u.role) || u.role === 'superadmin') : [];
    if (tabKey === 'admin') return isSuperAdmin ? orderedUsers.filter(u => adminRoles.includes(u.role)) : orderedUsers.filter(u => u.role === 'admin');
    return orderedUsers.filter(u => u.role === tabKey);
  };
  const selectedUserTile = useMemo(
    () => orderedUsers.find((item) => String(item.id) === String(selectedUserTileId)) || null,
    [orderedUsers, selectedUserTileId]
  );
  const getVisibleUsersForTab = (tabKey) => {
    const tabUsers = getUsersForTab(tabKey);
    if (!selectedUserTileId) return tabUsers;
    return tabUsers.filter((item) => String(item.id) === String(selectedUserTileId));
  };
  const getSearchedUsersForTab = (tabKey) => {
    const tabUsers = getVisibleUsersForTab(tabKey);
    const query = userListSearch.trim().toLowerCase();
    if (!query) return tabUsers;
    return tabUsers.filter((item) => [
      item.name,
      item.mobile,
      item.email,
      item.username,
      item.role,
      formatRole(item.role),
      getCreatedByName(item),
      item.is_active ? 'active' : 'inactive',
      item.created_at ? formatDate(item.created_at) : ''
    ].filter(Boolean).join(' ').toLowerCase().includes(query));
  };
  const getSelectedFilteredUsers = (items = []) => {
    if (!selectedUserTileId) return items;
    return items.filter((item) => String(item.id) === String(selectedUserTileId));
  };
  const selectUserTile = (userId) => {
    const nextUserTileId = String(userId || '');
    const isClearing = String(selectedUserTileId) === nextUserTileId;
    setSelectedUserTileId(isClearing ? '' : nextUserTileId);
    if (!isClearing && nextUserTileId) {
      setShowForm(false);
      setEditUser(null);
      setReportPreview(null);
      setTemporaryLoginResult(null);
      setActiveTab('all');
    }
  };
  const clearUserTileFilter = () => setSelectedUserTileId('');

  useEffect(() => {
    if (selectedUserTileId && !orderedUsers.some((item) => String(item.id) === String(selectedUserTileId))) {
      setSelectedUserTileId('');
    }
  }, [orderedUsers, selectedUserTileId]);

  const groupedAdmins = isSuperAdmin
    ? orderedUsers.filter(u => adminRoles.includes(u.role))
    : isWorkspaceAdmin
      ? [{
        id: user.id,
        name: user.name || 'Admin',
        mobile: user.mobile || '',
        email: user.email || '',
        username: user.username || user.mobile || '',
        role: user.role,
        is_active: user.is_active ?? 1,
        profile_pic: user.profile_pic,
        created_at: user.created_at || new Date().toISOString(),
      }]
      : [];
  const canManageUser = (target) => {
    if (!target) return false;
    if (target.role === 'superadmin') return false;
    if (isSuperAdmin) return true;
    if (!isWorkspaceAdmin) return false;
    return ['employee', 'dealer', 'customer'].includes(target.role);
  };
  const isUnderCurrentAdmin = (target) => {
    if (!target || !user?.id) return false;
    if (Number(target.created_by) === Number(user.id)) return true;
    const parent = orderedUsers.find(item => Number(item.id) === Number(target.created_by));
    return parent?.role === 'employee' && Number(parent.created_by) === Number(user.id);
  };
  const canEnterUser = (target) => {
    if (!target || !target.is_active) return false;
    if (isExpiredDemoAccount(target)) return false;
    if (Number(target.id) === Number(user?.id)) return false;
    if (target.role === 'superadmin') return false;
    if (isSuperAdmin) return true;
    if (!isWorkspaceAdmin || !teamRoles.includes(target.role)) return false;
    return isUnderCurrentAdmin(target);
  };

  const fetchWarehouseMapping = useCallback(async () => {
    if (!['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role)) return;
    try {
      const [warehouseRes, assignmentRes] = await Promise.all([
        axios.get(`${API_URL}/api/inventory/warehouses`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/warehouse-assignments`, { headers: getHeaders() })
      ]);
      const nextWarehouses = warehouseRes.data || [];
      const nextAssignments = assignmentRes.data || [];
      setWarehouses(nextWarehouses);
      setWarehouseAssignments(nextAssignments);
      return { warehouses: nextWarehouses, assignments: nextAssignments };
    } catch (err) {
      console.log(err);
      return null;
    }
  }, [getHeaders, user?.role]);

  useEffect(() => { fetchWarehouseMapping(); }, [fetchWarehouseMapping]);

  const getAssignedWarehouseIds = (employeeId, assignments = warehouseAssignments) => assignments
    .filter((assignment) => Number(assignment.employee_id) === Number(employeeId))
    .map((assignment) => String(assignment.warehouse_id));

  const activeEmployees = orderedUsers
    .filter((item) => item.role === 'employee' && Number(item.is_active ?? 1) === 1)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const selectedMappingEmployee = activeEmployees.find((item) => String(item.id) === String(mappingEmployeeId));
  const employeeWarehouseOptions = selectedMappingEmployee
    ? warehouses.filter((warehouse) => (
      !isSuperAdmin || Number(warehouse.admin_id || 0) === Number(selectedMappingEmployee.created_by || 0)
    ))
    : (isSuperAdmin ? [] : warehouses);

  const selectWarehouseMappingEmployee = (employeeId) => {
    setMappingEmployeeId(String(employeeId || ''));
    setMappingWarehouseIds(employeeId ? getAssignedWarehouseIds(employeeId) : []);
  };

  const openWarehouseMapping = async (employee = null, openCreate = false) => {
    if (!canMapWarehouses) return;
    setShowForm(false);
    setEditUser(null);
    setReportPreview(null);
    setShowWarehouseCreate(Boolean(openCreate));
    setActiveTab(openCreate ? 'warehouse-create' : 'warehouse-map');
    const latestMapping = await fetchWarehouseMapping();
    if (employee?.id) {
      setMappingEmployeeId(String(employee.id));
      setMappingWarehouseIds(getAssignedWarehouseIds(employee.id, latestMapping?.assignments || warehouseAssignments));
    }
  };

  const openWarehouseCreate = (employee = null) => {
    openWarehouseMapping(employee, true);
  };

  const toggleMappingWarehouse = (warehouseId) => {
    const id = String(warehouseId);
    setMappingWarehouseIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const resetWarehouseCreateForm = () => {
    setNewWarehouseName('');
    setNewWarehouseAddress('');
    setNewWarehouseNote('');
    setShowWarehouseCreate(false);
    if (activeTab === 'warehouse-create') setActiveTab('warehouse-map');
  };

  const createWarehouseFromUserManagement = async (event) => {
    event.preventDefault();
    const trimmedName = newWarehouseName.trim();
    if (!trimmedName) return alert('Office Location name is required');
    if (isSuperAdmin && !selectedMappingEmployee) {
      return alert('Super Admin must select an employee first. The office location will be created in that employee workspace.');
    }

    setWarehouseCreateLoading(true);
    try {
      const payload = {
        name: trimmedName,
        address: newWarehouseAddress,
        note: newWarehouseNote
      };
      if (isSuperAdmin) payload.admin_id = selectedMappingEmployee.created_by;

      const res = await axios.post(`${API_URL}/api/inventory/warehouses`, payload, { headers: getHeaders() });
      const createdWarehouseId = res.data?.warehouse?.id;
      await fetchWarehouseMapping();
      if (createdWarehouseId && selectedMappingEmployee) {
        setMappingWarehouseIds((current) => (
          current.includes(String(createdWarehouseId)) ? current : [...current, String(createdWarehouseId)]
        ));
      }
      resetWarehouseCreateForm();
      alert(selectedMappingEmployee ? 'Office Location created. Now save the mapping.' : 'Office Location created. Select an employee and save the mapping.');
    } catch (err) {
      alert(err.response?.data?.message || 'Office Location create failed');
    } finally {
      setWarehouseCreateLoading(false);
    }
  };

  const saveEmployeeWarehouseMapping = async () => {
    if (!selectedMappingEmployee) return alert('Please select an employee');
    if (employeeWarehouseOptions.length === 0) return alert('No office location found in this employee workspace');
    const previousWarehouseIds = new Set(getAssignedWarehouseIds(selectedMappingEmployee.id));
    const changedWarehouses = employeeWarehouseOptions.filter((warehouse) => (
      previousWarehouseIds.has(String(warehouse.id)) !== mappingWarehouseIds.includes(String(warehouse.id))
    ));
    if (changedWarehouses.length === 0) return alert('No changes found in office location mapping');
    setMappingLoading(true);
    try {
      await Promise.all(changedWarehouses.map((warehouse) => {
        const keepEmployeeIds = warehouseAssignments
          .filter((assignment) => (
            String(assignment.warehouse_id) === String(warehouse.id)
            && Number(assignment.employee_id) !== Number(selectedMappingEmployee.id)
          ))
          .map((assignment) => String(assignment.employee_id));
        const nextEmployeeIds = mappingWarehouseIds.includes(String(warehouse.id))
          ? [...keepEmployeeIds, String(selectedMappingEmployee.id)]
          : keepEmployeeIds;
        return axios.post(`${API_URL}/api/inventory/warehouses/${warehouse.id}/employees`, {
          employee_ids: nextEmployeeIds
        }, { headers: getHeaders() });
      }));
      await fetchWarehouseMapping();
      alert('Employee office location mapping saved!');
    } catch (err) {
      alert(err.response?.data?.message || 'Office location mapping save failed');
    } finally {
      setMappingLoading(false);
    }
  };

  const resetForm = (nextTab = 'grouped') => {
    setName('');
    setMobile('');
    setEmail('');
    setAddress('');
    setUsername('');
    setPassword('');
    setRole(defaultCreateRole);
    setEditUser(null);
    setShowForm(false);
    setActiveTab(nextTab);
  };

  const openCreatePanel = () => {
    setName('');
    setMobile('');
    setEmail('');
    setAddress('');
    setUsername('');
    setPassword('');
    setRole(defaultCreateRole);
    setEditUser(null);
    setShowForm(true);
    setReportPreview(null);
    setActiveTab('create');
  };

  const handleUserTabClick = (tabKey) => {
    setSelectedUserTileId('');
    if (tabKey === 'create') {
      openCreatePanel();
      return;
    }
    if (tabKey === 'warehouse-map') {
      openWarehouseMapping();
      return;
    }
    if (tabKey === 'warehouse-create') {
      openWarehouseCreate();
      return;
    }
    setShowForm(false);
    setEditUser(null);
    setReportPreview(null);
    setTemporaryLoginResult(null);
    setTemporaryPasswordCopied(false);
    setUserListPage(1);
    setActiveTab(tabKey);
  };

  useEffect(() => {
    const reportKey = USER_MANAGEMENT_REPORT_SUBMENU_TABS[userView];
    if (reportKey) {
      setShowForm(false);
      setEditUser(null);
      setTemporaryLoginResult(null);
      setTemporaryPasswordCopied(false);
      setActiveTab('reports');
      setReportPreview({
        key: reportKey,
        generatedAt: formatDateTime(new Date()),
      });
      return;
    }
    const tabKey = USER_MANAGEMENT_SUBMENU_TABS[userView];
    if (!tabKey) return;
    handleUserTabClick(tabKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userView]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editUser) {
        await axios.put(`${API_URL}/api/users/${editUser.id}`,
          { name, mobile, email, address, role, password: password || undefined }, { headers: getHeaders() });
        alert('User updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/users`,
          {
            name,
            mobile,
            email,
            address,
            username: username || mobile,
            password: role === 'customer' && !password ? mobile : password,
            role
          }, { headers: getHeaders() });
        alert('User created successfully!');
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createDemoAdmin = async () => {
    if (!isSuperAdmin || demoBusy) return;
    const nextDemoName = demoName.trim();
    if (!nextDemoName) {
      alert('Demo admin name is required');
      return;
    }
    if (!window.confirm(`Create demo admin "${nextDemoName}" active for exactly 5 days?`)) return;

    setDemoBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/users/demo-admin`, { name: nextDemoName }, { headers: getHeaders() });
      const createdDemo = {
        ...res.data,
        assigned_portal_url: getAssignedPortalUrl(res.data)
      };
      setDemoAccount(createdDemo);
      setDemoName('');
      await fetchUsers();
      alert(`Demo Admin Created\nName: ${createdDemo.name}\nID: ${createdDemo.username}\nPassword: ${createdDemo.password}\nPortal: ${createdDemo.assigned_portal_url}\nValid Till: ${formatDateTime(createdDemo.expires_at)}`);
    } catch (err) {
      alert(err.response?.data?.message || 'Demo admin create failed');
    } finally {
      setDemoBusy(false);
    }
  };

  const uploadProfilePic = async (userId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('profile_pic', file);
    try {
      await axios.post(`${API_URL}/api/profile/upload/${userId}`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      alert('Profile picture updated!');
      fetchUsers();
    } catch (err) {
      alert('Upload failed');
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setName(u.name || '');
    setMobile(u.mobile || '');
    setEmail(u.email || '');
    setAddress(u.address || '');
    setUsername(u.username || '');
    setRole(u.role || 'customer');
    setPassword('');
    setShowForm(true);
    setReportPreview(null);
    setTemporaryLoginResult(null);
    setTemporaryPasswordCopied(false);
    setActiveTab('create');
  };

  const copyTemporaryPassword = async () => {
    const passwordToCopy = temporaryLoginResult?.temporary_password;
    if (!passwordToCopy) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(passwordToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = passwordToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setTemporaryPasswordCopied(true);
      window.setTimeout(() => setTemporaryPasswordCopied(false), 1600);
    } catch (err) {
      alert('Copy failed. Please copy password manually.');
    }
  };

  const generateTemporaryLogin = async (target) => {
    if (!isSuperAdmin || !target || target.role === 'superadmin') return;
    if (!window.confirm(`Generate temporary login password for ${target.name || target.mobile}? Original password will not change.`)) return;

    try {
      const res = await axios.post(`${API_URL}/api/users/${target.id}/temporary-login`, {}, { headers: getHeaders() });
      setTemporaryLoginResult(res.data);
      setTemporaryPasswordCopied(false);
      alert(`Temporary Login Password Generated & 2FA Reset\nName: ${res.data.name}\nUsername: ${res.data.username || res.data.mobile}\nPassword: ${res.data.temporary_password}\nValid for: ${res.data.expires_in_minutes || 60} minutes`);
    } catch (err) {
      alert(err.response?.data?.message || 'Temporary login failed');
    }
  };

  const resetUser2FA = async (target) => {
    if (!isSuperAdmin || !target || target.role === 'superadmin') return;
    const label = target.name || target.username || target.mobile || `User #${target.id}`;
    if (!window.confirm(`Reset Google Authenticator 2FA for ${label}?\n\nThey will be required to scan a new QR code on their next login.`)) return;

    try {
      const res = await axios.post(`${API_URL}/api/users/${target.id}/reset-2fa`, {}, { headers: getHeaders() });
      alert(res.data?.message || 'Google Authenticator 2FA reset successfully!');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reset 2FA');
    }
  };

  const cleanUserDetails = async (target) => {
    if (!isSuperAdmin || !target || target.role === 'superadmin') return;
    const label = target.name || target.mobile || `User #${target.id}`;
    if (!window.confirm(`Clean details for ${label}? This will remove personal details, disable login, and keep only a cleaned account record.`)) return;

    try {
      await axios.patch(`${API_URL}/api/users/${target.id}/clean-details`, {}, { headers: getHeaders() });
      alert('User details cleaned successfully');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Clean failed');
    }
  };

  const deleteUserAccount = async (target) => {
    if (!isSuperAdmin || !target || target.role === 'superadmin') return;
    const label = target.name || target.mobile || `User #${target.id}`;
    const confirmation = window.prompt(`Delete ${label} and linked records permanently?\nType DELETE to confirm.`);
    if (confirmation !== 'DELETE') return;

    try {
      const res = await axios.delete(`${API_URL}/api/users/${target.id}`, { headers: getHeaders() });
      alert(res.data?.message || 'User deleted successfully');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const toggleUser = async (id, currentStatus) => {
    try {
      await axios.patch(`${API_URL}/api/users/${id}/toggle`,
        { is_active: currentStatus ? 0 : 1 }, { headers: getHeaders() });
      fetchUsers();
    } catch (err) {
      alert('Error updating user status');
    }
  };

  const enterUser = async (target) => {
    if (!target) return;
    if (!window.confirm(`Enter ${target.name || target.mobile}'s account?`)) return;

    try {
      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      const res = await axios.post(`${API_URL}/api/auth/impersonate/${target.id}`, {}, { headers: getHeaders() });

      pushImpersonationSession({ token: currentToken, user: currentUser });
      const switchedUser = setAuthSession({ token: res.data.token, user: res.data.user });
      window.dispatchEvent(new CustomEvent(AUTH_SESSION_CHANGED_EVENT, { detail: { user: switchedUser } }));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const message = err.response?.data?.message || 'Could not enter this account';
      if (err.response?.status === 401 || message === 'Invalid or expired token') {
        clearAuthSession();
        navigate('/login');
        return;
      }
      alert(message);
    }
  };

  const reportDefinitions = [
    { key: 'summary', label: 'User Summary' },
    { key: 'all', label: 'All Users Report' },
    ...(isSuperAdmin ? [{ key: 'admin', label: 'Admin Report' }, { key: 'whitelabel_admin', label: 'White Label Report' }] : []),
    ...(!isEmployee ? [{ key: 'employee', label: 'Employee Report' }] : []),
    { key: 'dealer', label: 'Dealer Report' },
    { key: 'customer', label: 'Customer Report' },
    { key: 'active', label: 'Active Users Report' },
    { key: 'inactive', label: 'Inactive Users Report' },
    ...(!isEmployee ? [{ key: 'by_admin', label: 'Admin Wise Report' }] : []),
  ];

  const userTabOrder = isEmployee
    ? ['all', 'dealer', 'customer', 'create', 'reports']
    : isWorkspaceAdmin
      ? ['all', 'employee', 'dealer', 'customer', 'warehouse-map', 'warehouse-create', 'create', 'reports']
    : isSuperAdmin
        ? ['grouped', 'all', 'admin', 'whitelabel_admin', 'employee', 'dealer', 'customer', 'warehouse-map', 'warehouse-create', 'create', 'master-search', 'reports']
        : ['grouped', 'all', 'employee', 'dealer', 'customer', 'warehouse-map', 'warehouse-create', 'reports'];
  const allUserTabs = [
    { key: 'grouped', label: isSuperAdmin ? 'Workspaces' : 'By Admin', count: groupedAdmins.length, Icon: ShieldCheck, tone: '#1d4ed8', tone2: '#14b8a6', soft: '#dbeafe', gradient: 'linear-gradient(135deg, #eff6ff 0%, #ccfbf1 100%)', shadow: 'rgba(29, 78, 216, 0.22)' },
    { key: 'all', label: 'All Users', count: users.length, Icon: Users, tone: '#475569', tone2: '#0ea5e9', soft: '#e2e8f0', gradient: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)', shadow: 'rgba(14, 165, 233, 0.2)' },
    { key: 'admin', label: 'Admins', count: isSuperAdmin ? orderedUsers.filter(u => adminRoles.includes(u.role)).length : roleCount('admin'), Icon: Building2, tone: '#0f766e', tone2: '#22c55e', soft: '#ccfbf1', gradient: 'linear-gradient(135deg, #ecfdf5 0%, #bbf7d0 100%)', shadow: 'rgba(15, 118, 110, 0.22)' },
    { key: 'whitelabel_admin', label: 'White Label', count: roleCount('whitelabel_admin'), Icon: Home, tone: '#0891b2', tone2: '#6366f1', soft: '#cffafe', gradient: 'linear-gradient(135deg, #ecfeff 0%, #e0e7ff 100%)', shadow: 'rgba(8, 145, 178, 0.22)' },
    { key: 'employee', label: 'Employees', count: roleCount('employee'), Icon: UserCheck, tone: '#15803d', tone2: '#84cc16', soft: '#dcfce7', gradient: 'linear-gradient(135deg, #f0fdf4 0%, #d9f99d 100%)', shadow: 'rgba(21, 128, 61, 0.22)' },
    { key: 'dealer', label: 'Dealers', count: roleCount('dealer'), Icon: Store, tone: '#7c3aed', tone2: '#ec4899', soft: '#ede9fe', gradient: 'linear-gradient(135deg, #f5f3ff 0%, #fce7f3 100%)', shadow: 'rgba(124, 58, 237, 0.22)' },
    { key: 'customer', label: 'Customers', count: roleCount('customer'), Icon: UserCog, tone: '#4d7c0f', tone2: '#f59e0b', soft: '#ecfccb', gradient: 'linear-gradient(135deg, #f7fee7 0%, #ffedd5 100%)', shadow: 'rgba(77, 124, 15, 0.22)' },
    ...(canMapWarehouses ? [{ key: 'warehouse-map', label: 'Office Location Map', count: 'MAP', Icon: ClipboardList, tone: '#0f766e', tone2: '#06b6d4', soft: '#ccfbf1', gradient: 'linear-gradient(135deg, #f0fdfa 0%, #cffafe 100%)', shadow: 'rgba(15, 118, 110, 0.22)' }] : []),
    ...(canMapWarehouses ? [{ key: 'warehouse-create', label: 'Create Office Location', count: '+', Icon: PackagePlus, tone: '#b45309', tone2: '#f97316', soft: '#fef3c7', gradient: 'linear-gradient(135deg, #fffbeb 0%, #fed7aa 100%)', shadow: 'rgba(180, 83, 9, 0.22)' }] : []),
    { key: 'create', label: editUser ? 'Edit User' : 'Create User', count: editUser ? 'EDIT' : '+', Icon: FilePlus2, tone: '#2563eb', tone2: '#8b5cf6', soft: '#dbeafe', gradient: 'linear-gradient(135deg, #eff6ff 0%, #ede9fe 100%)', shadow: 'rgba(37, 99, 235, 0.22)' },
    ...(isSuperAdmin ? [{ key: 'master-search', label: 'Master Search', count: 'FIND', Icon: Search, tone: '#334155', tone2: '#06b6d4', soft: '#e2e8f0', gradient: 'linear-gradient(135deg, #f8fafc 0%, #cffafe 100%)', shadow: 'rgba(51, 65, 85, 0.22)' }] : []),
    { key: 'reports', label: 'Reports', count: 'CSV', Icon: FileText, tone: '#be123c', tone2: '#f97316', soft: '#ffe4e6', gradient: 'linear-gradient(135deg, #fff1f2 0%, #ffedd5 100%)', shadow: 'rgba(190, 18, 60, 0.22)' },
  ];
  const userTabs = allUserTabs
    .filter((tab) => userTabOrder.includes(tab.key))
    .sort((a, b) => userTabOrder.indexOf(a.key) - userTabOrder.indexOf(b.key));
  const mainUserTabs = userTabs.filter((tab) => !['all', 'reports'].includes(tab.key));
  const reportsTab = userTabs.find((tab) => tab.key === 'reports');
  const showUserTileFilter = false;
  const showMainUserTabs = false;
  const showReportsOption = false;

  const userReportColumns = [
    { key: 'serial', label: '#' },
    { key: 'name', label: 'Name' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email', label: 'Email' },
    { key: 'username', label: 'Username' },
    { key: 'role', label: 'Role' },
    { key: 'created_by', label: 'Created By' },
    { key: 'status', label: 'Status' },
    ...(isSuperAdmin ? [{ key: 'demo_validity', label: 'Demo Valid Till' }] : []),
    { key: 'created', label: 'Created Date' },
  ];
  const userListPageSize = 5;
  const activeListUsers = listTabKeys.includes(activeTab) ? getSearchedUsersForTab(activeTab) : [];
  const userListPageCount = Math.max(1, Math.ceil(activeListUsers.length / userListPageSize));
  const safeUserListPage = Math.min(userListPage, userListPageCount);
  const userListStartIndex = (safeUserListPage - 1) * userListPageSize;
  const pagedListUsers = activeListUsers.slice(userListStartIndex, userListStartIndex + userListPageSize);
  const userManagementPageStyle = {
    height: '100%',
    maxHeight: '100%',
    padding: '24px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box'
  };
  const userManagementStickyHeaderStyle = {
    flex: '0 0 auto',
    position: 'relative',
    zIndex: 18,
    margin: '-24px -24px 0',
    padding: '8px 24px 12px',
    background: 'rgba(255, 255, 255, 0.96)',
    backdropFilter: 'blur(14px)',
    borderBottom: '1px solid #e5e7eb',
    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.06)'
  };
  const userManagementContentStyle = {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 2px',
    margin: '0 -2px',
    overscrollBehavior: 'contain',
    display: 'flex',
    flexDirection: 'column'
  };
  const userTablePanelStyle = {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    scrollMarginTop: '16px',
    flex: '1 1 auto',
    minHeight: 0
  };

  useEffect(() => {
    setUserListPage(1);
  }, [activeTab, selectedUserTileId, userListSearch]);

  useEffect(() => {
    if (userListPage > userListPageCount) setUserListPage(userListPageCount);
  }, [userListPage, userListPageCount]);

  const buildUserReportRow = (u, index) => ({
    serial: index + 1,
    name: u.name || '-',
    mobile: u.mobile || '-',
    email: u.email || '-',
    username: u.username || '-',
    role: formatRole(u.role),
    created_by: getCreatedByName(u),
    status: u.is_active ? 'Active' : 'Inactive',
    demo_validity: isDemoAdminAccount(u) ? formatDateTime(u.demo_expires_at) : '-',
    created: u.created_at ? formatDate(u.created_at) : '-',
  });

  const getReportData = (reportKey) => {
    const reportScopeUsers = getSelectedFilteredUsers(orderedUsers);

    if (reportKey === 'summary') {
      return {
        title: selectedUserTile ? `${selectedUserTile.name} Summary` : 'User Summary',
        columns: [
          { key: 'metric', label: 'Metric' },
          { key: 'count', label: 'Count' },
        ],
        rows: [
          { metric: 'Total Users', count: reportScopeUsers.length },
          { metric: 'Active Users', count: reportScopeUsers.filter(u => u.is_active).length },
          { metric: 'Inactive Users', count: reportScopeUsers.filter(u => !u.is_active).length },
          ...(isSuperAdmin ? [{ metric: 'Active Demo Admins', count: reportScopeUsers.filter(u => isDemoAdminAccount(u) && u.is_active).length }] : []),
          { metric: 'Admins', count: reportScopeUsers.filter(u => u.role === 'admin').length + (!isSuperAdmin ? reportScopeUsers.filter(u => u.role === 'whitelabel_admin').length : 0) },
          ...(isSuperAdmin ? [{ metric: 'White Label Admins', count: reportScopeUsers.filter(u => u.role === 'whitelabel_admin').length }] : []),
          { metric: 'Employees', count: reportScopeUsers.filter(u => u.role === 'employee').length },
          { metric: 'Dealers', count: reportScopeUsers.filter(u => u.role === 'dealer').length },
          { metric: 'Customers', count: reportScopeUsers.filter(u => u.role === 'customer').length },
        ],
      };
    }

    if (selectedUserTile && reportKey === 'by_admin') {
      return {
        title: `${selectedUserTile.name} Report`,
        columns: userReportColumns,
        rows: [buildUserReportRow(selectedUserTile, 0)],
      };
    }

    if (reportKey === 'by_admin') {
      return {
        title: 'Admin Wise User Report',
        columns: [
          { key: 'serial', label: '#' },
          { key: 'admin', label: 'Admin' },
          { key: 'mobile', label: 'Mobile' },
          { key: 'role', label: 'Role' },
          { key: 'workspace_accounts', label: 'Admin / White Label' },
          { key: 'employees', label: 'Employees' },
          { key: 'dealers', label: 'Dealers' },
          { key: 'customers', label: 'Customers' },
          { key: 'total', label: 'Total Team' },
          { key: 'status', label: 'Status' },
        ],
        rows: groupedAdmins.map((admin, index) => {
          const adminUsers = getAdminTreeUsers(admin.id);
          return {
            serial: index + 1,
            admin: admin.name || '-',
            mobile: admin.mobile || '-',
            role: formatRole(admin.role),
            workspace_accounts: adminUsers.filter(u => adminRoles.includes(u.role)).length,
            employees: adminUsers.filter(u => u.role === 'employee').length,
            dealers: adminUsers.filter(u => u.role === 'dealer').length,
            customers: adminUsers.filter(u => u.role === 'customer').length,
            total: adminUsers.filter(u => teamRoles.includes(u.role)).length,
            status: admin.is_active ? 'Active' : 'Inactive',
          };
        }),
      };
    }

    const baseReportUsers = reportKey === 'active'
      ? orderedUsers.filter(u => u.is_active)
      : reportKey === 'inactive'
        ? orderedUsers.filter(u => !u.is_active)
        : getUsersForTab(reportKey);
    const reportUsers = getSelectedFilteredUsers(baseReportUsers);
    const reportTitle = reportDefinitions.find(item => item.key === reportKey)?.label || 'User Report';

    return {
      title: selectedUserTile ? `${selectedUserTile.name} - ${reportTitle}` : reportTitle,
      columns: userReportColumns,
      rows: reportUsers.map(buildUserReportRow),
    };
  };

  const activeReportPreview = reportPreview ? {
    ...reportPreview,
    ...getReportData(reportPreview.key),
  } : null;

  const escapeCsvValue = (value) => {
    const text = String(value ?? '');
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const downloadUserReport = () => {
    if (!activeReportPreview) return;
    const csvLines = [
      activeReportPreview.columns.map(col => escapeCsvValue(col.label)).join(','),
      ...activeReportPreview.rows.map(row => activeReportPreview.columns.map(col => escapeCsvValue(row[col.key])).join(',')),
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${activeReportPreview.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'user-report'}.csv`;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  };

  const runMasterSearch = async (event) => {
    event?.preventDefault();
    const q = masterSearchTerm.trim();
    if (q.length < 2) {
      setMasterSearchError('Enter at least 2 characters');
      return;
    }
    setMasterSearchLoading(true);
    setMasterSearchError('');
    try {
      const res = await axios.get(`${API_URL}/api/users/master-search`, {
        headers: getHeaders(),
        params: { q }
      });
      setMasterSearchResult(res.data);
    } catch (err) {
      setMasterSearchResult(null);
      setMasterSearchError(err.response?.data?.message || 'Master Search failed');
    } finally {
      setMasterSearchLoading(false);
    }
  };

  const masterSections = [
    { key: 'users', label: 'User Login' },
    { key: 'vendors', label: 'Vendors' },
    { key: 'customers', label: 'Customers' },
    { key: 'sales', label: 'Sales' },
    { key: 'payments', label: 'Payments' },
    { key: 'purchases', label: 'Purchases' },
  ];

  const masterSectionCount = (key) => masterSearchResult?.sections?.[key]?.length || 0;
  const masterTotalCount = masterSections.reduce((sum, section) => sum + masterSectionCount(section.key), 0);

  const renderMasterDetail = (item) => {
    const entries = [
      ['Username', item.username],
      ['Email', item.email],
      ['Vehicle', item.vehicle_no],
      ['IMEI', item.imei],
      ['Invoice', item.invoice_no],
      ['Product', item.product_name],
      ['Amount', item.amount || item.total_amount],
      ['Paid', item.paid_amount || item.purchase_paid],
      ['Balance', item.balance_amount],
      ['Payment Mode', item.payment_mode],
      ['Payment Status', item.payment_status],
      ['Purchase Count', item.purchase_count],
      ['GST', item.gst_no],
      ['Dealer', item.dealer?.name || item.dealer_name],
      ['Tagged User', item.tagged_user_name],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');

    return entries;
  };

  const Avatar = ({ u, size = 36 }) => (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {u.profile_pic ? (
        <img src={`${API_URL}/api/profile/pic/${u.profile_pic}`} alt={u.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <span style={{ color: 'white', fontWeight: 'bold', fontSize: size / 2.5 }}>{u.name?.charAt(0).toUpperCase()}</span>
      )}
    </div>
  );

  const UserTileFilter = () => (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px', marginBottom: '18px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div>
          <h3 style={{ margin: '0 0 3px', color: '#1e3a5f', fontSize: '16px' }}>User Tiles</h3>
          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 700 }}>
            {selectedUserTile ? `Showing ${selectedUserTile.name}` : `${orderedUsers.length} users`}
          </div>
        </div>
        {selectedUserTile && (
          <button
            type="button"
            onClick={clearUserTileFilter}
            style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 800 }}
          >
            All Users
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(184px, 1fr))', gap: '10px', maxHeight: '258px', overflowY: 'auto', paddingRight: '2px' }}>
        {orderedUsers.map((item) => {
          const isSelected = String(item.id) === String(selectedUserTileId);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => selectUserTile(item.id)}
              style={{
                minHeight: '78px',
                padding: '10px',
                borderRadius: '10px',
                border: `1px solid ${isSelected ? '#0ea5e9' : '#e5e7eb'}`,
                background: isSelected ? '#f0f9ff' : '#f8fafc',
                boxShadow: isSelected ? '0 10px 20px rgba(14, 165, 233, 0.16)' : 'none',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minWidth: 0
              }}
            >
              <Avatar u={item} size={38} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', color: '#0f172a', fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name || '-'}</span>
                <span style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginTop: '4px' }}>
                  <span style={{ padding: '2px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: 800, background: roleColors[item.role]?.bg, color: roleColors[item.role]?.color }}>
                    {formatRole(item.role)}
                  </span>
                  <span style={{ padding: '2px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: 800, background: item.is_active ? '#dcfce7' : '#fee2e2', color: item.is_active ? '#166534' : '#b91c1c' }}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </span>
                <span style={{ display: 'block', marginTop: '4px', color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.mobile || item.username || '-'}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const UserActionButton = ({ children, onClick, tone, asLabel = false, fileUserId = null, title = '' }) => {
    const style = { ...actionButtonBase, ...(actionTones[tone] || actionTones.edit) };
    if (asLabel) {
      return (
        <label style={style} title={title || String(children || '')}>
          {children}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadProfilePic(fileUserId, e.target.files[0])} />
        </label>
      );
    }
    return (
      <button type="button" onClick={onClick} style={style} title={title || String(children || '')}>
        {children}
      </button>
    );
  };

  const UserActionTable = ({ actions }) => (
    <div style={actionBarStyle} aria-label="User actions">
      {actionOrder.filter((slot) => actions[slot]).map((slot) => (
        <span key={slot} style={{ display: 'inline-flex' }}>
          {actions[slot]}
        </span>
      ))}
    </div>
  );

  const renderUserActions = (u) => {
    if (!canManageUser(u)) {
      return <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 'bold' }}>{isEmployee ? 'Create only' : 'Locked'}</span>;
    }

    const actions = {};
    if (isSuperAdmin && isPortalAdminAccount(u)) {
      actions.portal = <UserActionButton tone="portal" onClick={() => openAssignedPortal(u)}>Portal</UserActionButton>;
    }
    if (canEnterUser(u)) {
      actions.enter = <UserActionButton tone="enter" onClick={() => enterUser(u)}>Enter</UserActionButton>;
    }
    if (isSuperAdmin && u.role !== 'superadmin') {
      actions.temporary = <UserActionButton tone="temporary" onClick={() => generateTemporaryLogin(u)}>Temporary Login</UserActionButton>;
      actions.reset2fa = <UserActionButton tone="reset2fa" onClick={() => resetUser2FA(u)} title="Reset Google Authenticator 2FA">Reset 2FA</UserActionButton>;
      actions.clean = <UserActionButton tone="clean" onClick={() => cleanUserDetails(u)}>Clean</UserActionButton>;
      actions.delete = <UserActionButton tone="delete" onClick={() => deleteUserAccount(u)}>Delete</UserActionButton>;
    }
    if (canMapWarehouses && u.role === 'employee') {
      const officeLocationText = getOfficeLocation(u);
      const officeLocationLabel = officeLocationText && officeLocationText !== '-' ? officeLocationText : 'No Office Location';
      actions.warehouse = (
        <UserActionButton tone="warehouse" onClick={() => openWarehouseMapping(u)} title={officeLocationLabel}>
          {officeLocationLabel}
        </UserActionButton>
      );
    }
    actions.edit = <UserActionButton tone="edit" onClick={() => openEdit(u)}>Edit</UserActionButton>;
    actions.photo = <UserActionButton tone="photo" asLabel fileUserId={u.id}>Photo</UserActionButton>;
    actions.status = (
      <UserActionButton tone={u.is_active ? 'disable' : 'enable'} onClick={() => toggleUser(u.id, u.is_active)}>
        {u.is_active ? 'Disable' : 'Enable'}
      </UserActionButton>
    );

    return <UserActionTable actions={actions} />;
  };

  const UserRows = ({ data, showRole = true, showCreatedBy = false, showOfficeLocation = false, showCreatedDate = true, startIndex = 0 }) => data.map((u, i) => (
    <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
      <td style={{ padding: '10px 14px', fontSize: '13px' }}>{startIndex + i + 1}</td>
      <td style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar u={u} size={32} />
          <div>
            <span style={{ fontSize: '13px', fontWeight: 'bold' }}>{u.name}</span>
            {isDemoAdminAccount(u) && (
              <div style={{ marginTop: '2px', color: '#b45309', fontSize: '11px', fontWeight: 'bold' }}>
                Demo till {formatDateTime(u.demo_expires_at)}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={{ padding: '10px 14px', fontSize: '13px' }}>{u.mobile}</td>
      <td style={{ padding: '10px 14px', fontSize: '13px' }}>
        <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>{u.username}</code>
      </td>
      {showRole && (
        <td style={{ padding: '10px 14px' }}>
          <span style={{ padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', background: roleColors[u.role]?.bg, color: roleColors[u.role]?.color }}>
            {formatRole(u.role)}
          </span>
        </td>
      )}
      {showCreatedBy && <td style={{ padding: '10px 14px', fontSize: '13px' }}>{getCreatedByName(u)}</td>}
      {showOfficeLocation && <td style={{ padding: '10px 14px', fontSize: '12px', color: '#475569', maxWidth: '180px', overflowWrap: 'anywhere' }}>{getOfficeLocation(u)}</td>}
      <td style={{ padding: '10px 14px' }}>
        <span style={{ padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', background: u.is_active ? '#d1fae5' : '#fee2e2', color: u.is_active ? '#065f46' : '#dc2626' }}>
          {u.is_active ? 'Active' : 'Inactive'}
        </span>
        {isDemoAdminAccount(u) && (
          <span style={{ display: 'inline-block', marginLeft: '6px', padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', background: '#fef3c7', color: '#92400e' }}>
            5 Day Demo
          </span>
        )}
      </td>
      {showCreatedDate && <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7280' }}>{u.created_at ? formatDate(u.created_at) : '-'}</td>}
      <td style={actionColumnCellStyle}>
        {renderUserActions(u)}
      </td>
    </tr>
  ));

  const UserTable = ({ data, headerBg = '#1e3a5f', headerColor = 'white', showRole = true, showCreatedBy = false, showOfficeLocation = false, showCreatedDate = true, emptyText = 'No users found', startIndex = 0, headerControls = null }) => {
    const headers = [
      '#',
      'Name',
      'Mobile',
      'Username',
      ...(showRole ? ['Role'] : []),
      ...(showCreatedBy ? ['Created By'] : []),
      ...(showOfficeLocation ? ['Office Location'] : []),
      'Status',
      ...(showCreatedDate ? ['Created'] : []),
      'Actions'
    ];
    const tableMinWidth = showCreatedBy
      ? (showOfficeLocation ? '1460px' : '1320px')
      : (showOfficeLocation ? '1260px' : '1100px');

    return (
      <div style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: tableMinWidth, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: headerBg, color: headerColor }}>
                {headers.map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', ...(h === 'Actions' ? actionHeaderStyle : {}) }}>
                    {h === 'Actions' && headerControls ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minWidth: 0 }}>
                        <span>{h}</span>
                        {headerControls}
                      </span>
                    ) : h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                    {emptyText}
                  </td>
                </tr>
              ) : (
                <UserRows data={data} showRole={showRole} showCreatedBy={showCreatedBy} showOfficeLocation={showOfficeLocation} showCreatedDate={showCreatedDate} startIndex={startIndex} />
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={userManagementPageStyle}>
      <div style={userManagementStickyHeaderStyle}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ color: '#1e3a5f', margin: 0 }}>User Management</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginLeft: 'auto' }}>
            {isSuperAdmin && (
              <>
                <input
                  style={{ ...inp, width: '220px', padding: '10px 12px' }}
                  value={demoName}
                  onChange={e => setDemoName(e.target.value)}
                  placeholder="Demo admin name"
                  maxLength={120}
                  disabled={demoBusy}
                />
                <button
                  type="button"
                  onClick={createDemoAdmin}
                  disabled={demoBusy}
                  style={{
                    background: demoBusy ? '#94a3b8' : '#f59e0b',
                    color: 'white',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    cursor: demoBusy ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    boxShadow: '0 8px 18px rgba(245,158,11,0.24)'
                  }}
                >
                  {demoBusy ? 'Creating Demo...' : 'Create 5 Day Demo Admin'}
                </button>
              </>
            )}
            {listTabKeys.includes(activeTab) && (
              <input
                value={userListSearch}
                onChange={(event) => setUserListSearch(event.target.value)}
                placeholder="Search report"
                style={{ ...inp, width: '230px', padding: '10px 12px', background: 'white', borderColor: '#bfdbfe' }}
              />
            )}
          </div>
        </div>
        {loadError && (
          <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '14px', fontWeight: 'bold' }}>
            {loadError}
          </div>
        )}

        {demoAccount && (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '14px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: '#92400e', fontWeight: 'bold', marginBottom: '4px' }}>Demo Admin Active</div>
              <div style={{ color: '#78350f', fontSize: '13px' }}>Valid till {formatDateTime(demoAccount.expires_at)}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <code style={{ background: 'white', border: '1px solid #fbbf24', borderRadius: '6px', padding: '7px 10px', color: '#92400e', fontSize: '12px' }}>Name: {demoAccount.name}</code>
              <code style={{ background: 'white', border: '1px solid #fbbf24', borderRadius: '6px', padding: '7px 10px', color: '#92400e', fontSize: '12px' }}>ID: {demoAccount.username}</code>
              <code style={{ background: 'white', border: '1px solid #fbbf24', borderRadius: '6px', padding: '7px 10px', color: '#92400e', fontSize: '12px' }}>Password: {demoAccount.password}</code>
              <a href={getAssignedPortalUrl(demoAccount)} target="_blank" rel="noreferrer" style={{ background: 'white', border: '1px solid #fbbf24', borderRadius: '6px', padding: '7px 10px', color: '#92400e', fontSize: '12px', fontFamily: 'monospace', textDecoration: 'none' }}>Portal: {getAssignedPortalUrl(demoAccount)}</a>
            </div>
          </div>
        )}

        {temporaryLoginResult && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '14px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: '#c2410c', fontWeight: 'bold', marginBottom: '4px' }}>Temporary Login Password Generated</div>
              <div style={{ color: '#9a3412', fontSize: '13px' }}>
                {temporaryLoginResult.name} can login with this password for {temporaryLoginResult.expires_in_minutes || 60} minutes. Original password is unchanged.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <code style={{ background: 'white', border: '1px solid #fed7aa', borderRadius: '6px', padding: '7px 10px', color: '#9a3412', fontSize: '12px' }}>Username: {temporaryLoginResult.username || temporaryLoginResult.mobile}</code>
              <code style={{ background: 'white', border: '1px solid #fed7aa', borderRadius: '6px', padding: '7px 10px', color: '#9a3412', fontSize: '12px' }}>Password: {temporaryLoginResult.temporary_password}</code>
              <button
                type="button"
                onClick={copyTemporaryPassword}
                style={{
                  background: temporaryPasswordCopied ? '#dcfce7' : '#ffedd5',
                  color: temporaryPasswordCopied ? '#15803d' : '#9a3412',
                  border: `1px solid ${temporaryPasswordCopied ? '#bbf7d0' : '#fed7aa'}`,
                  borderRadius: '6px',
                  padding: '7px 10px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Copy size={13} strokeWidth={2.4} />
                {temporaryPasswordCopied ? 'Copied' : 'Copy Password'}
              </button>
              <button type="button" onClick={() => { setTemporaryLoginResult(null); setTemporaryPasswordCopied(false); }} style={{ background: '#ffedd5', color: '#9a3412', border: 'none', borderRadius: '6px', padding: '7px 10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                Hide
              </button>
            </div>
          </div>
        )}

        {showUserTileFilter && <UserTileFilter />}

        {showMainUserTabs && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: '7px', marginBottom: 0 }}>
          {mainUserTabs.map(tab => {
            const isActive = activeTab === tab.key;
            const Icon = tab.Icon;
            return (
              <button key={tab.key} type="button" onClick={() => handleUserTabClick(tab.key)} style={{
                minHeight: '58px',
                padding: '7px 8px',
                borderRadius: '8px',
                border: `1px solid ${isActive ? tab.tone : 'rgba(148, 163, 184, 0.35)'}`,
                cursor: 'pointer',
                background: isActive ? tab.gradient : `linear-gradient(135deg, #ffffff 0%, ${tab.soft} 100%)`,
                color: '#0f172a',
                boxShadow: isActive ? `0 8px 16px ${tab.shadow}` : '0 4px 10px rgba(15,23,42,0.06)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '6px',
                textAlign: 'left',
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease'
              }}>
                <span style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '3px', background: `linear-gradient(90deg, ${tab.tone}, ${tab.tone2})` }} />
                <span style={{ position: 'absolute', right: '-14px', bottom: '-16px', width: '42px', height: '42px', borderRadius: '50%', background: tab.tone2, opacity: isActive ? 0.14 : 0.08 }} />
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px', width: '100%' }}>
                  <span style={{ width: '27px', height: '27px', borderRadius: '8px', background: `linear-gradient(135deg, ${tab.tone}, ${tab.tone2})`, color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', boxShadow: `0 4px 10px ${tab.shadow}`, position: 'relative', zIndex: 1 }}>
                    <Icon size={14} strokeWidth={2.2} />
                  </span>
                  <span style={{ minWidth: '24px', padding: '2px 6px', borderRadius: '999px', background: isActive ? tab.tone : 'rgba(255,255,255,0.78)', color: isActive ? 'white' : tab.tone, border: `1px solid ${isActive ? tab.tone : 'rgba(255,255,255,0.92)'}`, fontSize: '10px', fontWeight: 'bold', textAlign: 'center', boxShadow: '0 2px 6px rgba(15,23,42,0.07)', position: 'relative', zIndex: 1 }}>
                    {tab.count}
                  </span>
                </span>
                <span style={{ display: 'block', fontSize: '11px', fontWeight: 800, lineHeight: 1.15, color: isActive ? tab.tone : '#0f172a', position: 'relative', zIndex: 1 }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
        )}
      </div>

      <div style={userManagementContentStyle}>
      {activeTab === 'create' && showForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#1e3a5f', marginTop: 0 }}>{editUser ? `Edit - ${editUser.name}` : 'Create User Account'}</h3>
          {(!editUser || isSuperAdmin) && (
            <div style={{ marginBottom: '18px' }}>
              <label style={lbl}>Account Type</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {roleOptions.map(r => (
                  <button key={r} type="button" onClick={() => setRole(r)} style={{ padding: '9px 14px', borderRadius: '999px', border: `1px solid ${role === r ? roleColors[r]?.color : '#d1d5db'}`, background: role === r ? roleColors[r]?.bg : 'white', color: role === r ? roleColors[r]?.color : '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                    {formatRole(r)}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px' }}>
                <span style={{ padding: '4px 10px', borderRadius: '999px', background: roleColors[role]?.bg, color: roleColors[role]?.color, fontSize: '12px', fontWeight: 'bold' }}>{formatRole(role)}</span>
                <small style={{ color: '#64748b' }}>{createRoleDescriptions[role]}</small>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div><label style={lbl}>Full Name *</label><input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="Full name" required /></div>
              <div><label style={lbl}>Mobile *</label><input style={inp} value={mobile} onChange={e => setMobile(e.target.value)} placeholder="Mobile number" required /></div>
              <div><label style={lbl}>Email</label><input style={inp} value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" /></div>
              <div><label style={lbl}>Address</label><input style={inp} value={address} onChange={e => setAddress(e.target.value)} placeholder="Address for invoice" /></div>
              {!editUser && <div><label style={lbl}>Internal Username</label><input style={inp} value={username} onChange={e => setUsername(e.target.value)} placeholder="Optional; mobile remains login" /></div>}
              <div>
                <label style={lbl}>{editUser ? 'New Password (leave blank to keep)' : role === 'customer' ? 'Password (mobile auto)' : 'Password *'}</label>
                <input type="password" style={inp} value={password} onChange={e => setPassword(e.target.value)} placeholder={editUser ? 'Leave blank to keep current' : role === 'customer' ? 'Blank means mobile number' : 'Set password'} required={!editUser && role !== 'customer'} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
              <button type="submit" disabled={loading} style={{ background: '#10b981', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '15px', cursor: 'pointer', fontWeight: 'bold' }}>
                {loading ? 'Saving...' : editUser ? 'Update User' : `Create ${formatRole(role)}`}
              </button>
              <button type="button" onClick={() => resetForm()} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '15px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {['warehouse-map', 'warehouse-create'].includes(activeTab) && canMapWarehouses && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '22px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderLeft: '5px solid #0f766e' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div>
              <h3 style={{ margin: '0 0 4px', color: '#0f766e' }}>Employee Office Location Mapping</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>Select the office locations this employee can view or transfer stock from, then save.</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => openWarehouseCreate()} style={{ background: '#0f766e', color: 'white', border: 'none', padding: '9px 13px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                + Create Office Location
              </button>
              <button type="button" onClick={fetchWarehouseMapping} style={{ background: '#e0f2fe', color: '#0369a1', border: 'none', padding: '9px 13px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                Refresh
              </button>
            </div>
          </div>

          {(showWarehouseCreate || activeTab === 'warehouse-create') && (
            <form onSubmit={createWarehouseFromUserManagement} style={{ marginBottom: '18px', border: '1px solid #99f6e4', borderRadius: '12px', padding: '16px', background: '#f0fdfa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div>
                  <h4 style={{ margin: '0 0 3px', color: '#0f766e' }}>Create Office Location</h4>
                  {isSuperAdmin && selectedMappingEmployee && (
                    <p style={{ margin: 0, color: '#475569', fontSize: '12px' }}>
                      This office location will be created in {selectedMappingEmployee.name}'s workspace.
                    </p>
                  )}
                  {isSuperAdmin && !selectedMappingEmployee && (
                    <p style={{ margin: 0, color: '#dc2626', fontSize: '12px', fontWeight: 'bold' }}>
                      Select an employee first. The office location will be created in that employee workspace.
                    </p>
                  )}
                </div>
                <button type="button" onClick={resetWarehouseCreateForm} style={{ background: '#e5e7eb', color: '#374151', border: 'none', padding: '7px 11px', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                  Cancel
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={lbl}>Office Location Name *</label>
                  <input style={inp} value={newWarehouseName} onChange={(event) => setNewWarehouseName(event.target.value)} placeholder="Main Office Location" />
                </div>
                <div>
                  <label style={lbl}>Address</label>
                  <input style={inp} value={newWarehouseAddress} onChange={(event) => setNewWarehouseAddress(event.target.value)} placeholder="Office location address" />
                </div>
                <div>
                  <label style={lbl}>Note</label>
                  <input style={inp} value={newWarehouseNote} onChange={(event) => setNewWarehouseNote(event.target.value)} placeholder="Optional note" />
                </div>
              </div>
              <button type="submit" disabled={warehouseCreateLoading} style={{ marginTop: '12px', background: warehouseCreateLoading ? '#94a3b8' : '#0f766e', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: warehouseCreateLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                {warehouseCreateLoading ? 'Saving...' : 'Save Office Location'}
              </button>
            </form>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 360px) 1fr', gap: '16px', alignItems: 'start' }}>
            <div>
              <label style={lbl}>Employee *</label>
              <select style={inp} value={mappingEmployeeId} onChange={(event) => selectWarehouseMappingEmployee(event.target.value)}>
                <option value="">Select employee</option>
                {activeEmployees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} - {employee.mobile || employee.username}{isSuperAdmin ? ` - ${getCreatedByName(employee)}` : ''}
                  </option>
                ))}
              </select>
              {activeEmployees.length === 0 && (
                <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '13px', fontWeight: 'bold' }}>No active employee found. Create an employee first.</div>
              )}
            </div>

            <div>
              <label style={lbl}>Office Location Access *</label>
              {!selectedMappingEmployee ? (
                <div style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '18px', color: '#64748b', fontSize: '13px' }}>
                  Select an employee first.
                </div>
              ) : employeeWarehouseOptions.length === 0 ? (
                <div style={{ border: '1px dashed #fecaca', borderRadius: '10px', padding: '18px', color: '#dc2626', fontSize: '13px', fontWeight: 'bold' }}>
                  No office location found in this employee workspace.
                  <button type="button" onClick={() => openWarehouseCreate(selectedMappingEmployee)} style={{ display: 'block', marginTop: '10px', background: '#0f766e', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    + Create Office Location
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setMappingWarehouseIds(employeeWarehouseOptions.map((warehouse) => String(warehouse.id)))} style={{ background: '#dcfce7', color: '#15803d', border: 'none', padding: '7px 10px', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>Select All</button>
                    <button type="button" onClick={() => setMappingWarehouseIds([])} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '7px 10px', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>Clear</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px' }}>
                    {employeeWarehouseOptions.map((warehouse) => {
                      const checked = mappingWarehouseIds.includes(String(warehouse.id));
                      return (
                        <label key={warehouse.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', border: `1px solid ${checked ? '#0f766e' : '#e5e7eb'}`, borderRadius: '9px', background: checked ? '#f0fdfa' : '#fff', cursor: 'pointer' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleMappingWarehouse(warehouse.id)} />
                          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>
                            {warehouse.name}
                            <small style={{ display: 'block', marginTop: '2px', color: '#64748b', fontWeight: 'normal' }}>
                              Stock: {warehouse.total_stock || 0} | Employees: {warehouse.assigned_employee_names || '-'}
                            </small>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" onClick={saveEmployeeWarehouseMapping} disabled={mappingLoading || !selectedMappingEmployee} style={{ background: mappingLoading || !selectedMappingEmployee ? '#94a3b8' : '#0f766e', color: 'white', border: 'none', padding: '11px 18px', borderRadius: '8px', cursor: mappingLoading || !selectedMappingEmployee ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
              {mappingLoading ? 'Saving...' : 'Save Mapping'}
            </button>
            {selectedMappingEmployee && (
              <span style={{ color: '#475569', fontSize: '13px' }}>
                {selectedMappingEmployee.name} will have {mappingWarehouseIds.length} office location(s) mapped.
              </span>
            )}
          </div>

          {warehouseAssignments.length > 0 && (
            <div style={{ marginTop: '18px', borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
              <h4 style={{ margin: '0 0 10px', color: '#1e3a5f' }}>Current Mapping</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
                {warehouseAssignments.slice(0, 12).map((assignment) => (
                  <div key={assignment.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '9px 10px', background: '#f8fafc', fontSize: '12px' }}>
                    <strong style={{ color: '#0f766e' }}>{assignment.employee_name}</strong>
                    <span style={{ color: '#475569' }}> - {assignment.warehouse_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'master-search' && isSuperAdmin && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '22px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderLeft: '5px solid #334155' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div>
              <h3 style={{ margin: '0 0 4px', color: '#1e3a5f' }}>Master Search</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>Find who owns any user, vendor, customer, sale, payment, or purchase record.</p>
            </div>
            {masterSearchResult && (
              <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '999px', padding: '7px 11px', fontSize: '12px', fontWeight: 900 }}>
                {masterTotalCount} result(s)
              </span>
            )}
          </div>

          <form onSubmit={runMasterSearch} style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) auto', gap: '10px', alignItems: 'end', marginBottom: '16px' }}>
            <div>
              <label style={lbl}>Name / Mobile / Username / Vehicle / Invoice</label>
              <input
                style={inp}
                value={masterSearchTerm}
                onChange={(event) => setMasterSearchTerm(event.target.value)}
                placeholder="Example: KAMAL, 9354330200, invoice no, vehicle no"
              />
            </div>
            <button type="submit" disabled={masterSearchLoading} style={{ background: masterSearchLoading ? '#94a3b8' : '#334155', color: 'white', border: 'none', padding: '11px 17px', borderRadius: '8px', cursor: masterSearchLoading ? 'wait' : 'pointer', fontWeight: 'bold', minHeight: '40px' }}>
              {masterSearchLoading ? 'Searching...' : 'Search'}
            </button>
          </form>

          {masterSearchError && (
            <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', fontWeight: 800 }}>
              {masterSearchError}
            </div>
          )}

          {masterSearchResult && (
            <div style={{ display: 'grid', gap: '14px' }}>
              {masterSections.map((section) => {
                const rows = masterSearchResult.sections?.[section.key] || [];
                if (rows.length === 0) return null;
                return (
                  <div key={section.key} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ background: '#f8fafc', padding: '10px 13px', display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                      <strong style={{ color: '#1e3a5f', fontSize: '14px' }}>{section.label}</strong>
                      <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 900 }}>{rows.length}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px', padding: '12px' }}>
                      {rows.map((item) => (
                        <div key={`${section.key}-${item.id}`} style={{ border: '1px solid #e5e7eb', borderRadius: '9px', padding: '12px', background: '#fff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div style={{ minWidth: 0 }}>
                              <strong style={{ display: 'block', color: '#0f172a', fontSize: '14px', wordBreak: 'break-word' }}>{item.name || '-'}</strong>
                              <span style={{ display: 'block', color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{item.mobile || 'No mobile'}</span>
                            </div>
                            <span style={{ flex: '0 0 auto', background: item.status === 'Inactive' ? '#fee2e2' : '#dcfce7', color: item.status === 'Inactive' ? '#b91c1c' : '#166534', borderRadius: '999px', padding: '3px 8px', fontSize: '11px', fontWeight: 900 }}>
                              {item.status || item.type}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gap: '5px', marginBottom: '8px' }}>
                            <div style={{ color: '#334155', fontSize: '12px' }}><b>Type:</b> {item.type} {item.role ? `(${formatRole(item.role)})` : ''}</div>
                            <div style={{ color: '#334155', fontSize: '12px' }}><b>Owner:</b> {item.owner ? `${item.owner.name} (${formatRole(item.owner.role)}) - ${item.owner.mobile || '-'}` : '-'}</div>
                            <div style={{ color: '#334155', fontSize: '12px' }}><b>Created By:</b> {item.created_by ? `${item.created_by.name} (${formatRole(item.created_by.role)}) - ${item.created_by.mobile || '-'}` : '-'}</div>
                          </div>

                          {renderMasterDetail(item).length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {renderMasterDetail(item).slice(0, 10).map(([label, value]) => (
                                <span key={label} style={{ background: '#f1f5f9', color: '#334155', borderRadius: '7px', padding: '4px 7px', fontSize: '11px', fontWeight: 800 }}>
                                  {label}: {String(value)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {masterTotalCount === 0 && (
                <div style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '28px', textAlign: 'center', color: '#64748b', fontSize: '14px', fontWeight: 800 }}>
                  No matching account, vendor, customer, sale, payment, or purchase found.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'grouped' && selectedUserTile && (
        <div id="user-filter-results" style={userTablePanelStyle}>
          <UserTable
            data={[selectedUserTile]}
            showRole={true}
            showCreatedBy={true}
            showCreatedDate={true}
            emptyText="Selected user not found"
          />
        </div>
      )}

      {activeTab === 'grouped' && !selectedUserTile && (
        <div>
          {groupedAdmins.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#666' }}>No admin workspaces found. Create an admin first!</div>
          ) : groupedAdmins.map(admin => {
            const directAdminUsers = getUsersByAdmin(admin.id);
            const workspaceAccounts = directAdminUsers.filter(u => adminRoles.includes(u.role));
            const workspaceTreeUsers = workspaceAccounts.flatMap(account => getAdminTreeUsers(account.id));
            const adminUsers = isSuperAdmin ? [...directAdminUsers, ...workspaceTreeUsers] : getAdminTreeUsers(admin.id);
            const teamUsers = isSuperAdmin ? workspaceTreeUsers : adminUsers;
            const employees = teamUsers.filter(u => u.role === 'employee');
            const dealers = teamUsers.filter(u => u.role === 'dealer');
            const customers = teamUsers.filter(u => u.role === 'customer');
            return (
              <div key={admin.id} style={{ marginBottom: '28px', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', background: 'white' }}>
                <div style={{ background: admin.is_active ? '#1e3a5f' : '#6b7280', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Avatar u={admin} size={44} />
                    <div>
                      <span style={{ color: 'white', fontWeight: 'bold', fontSize: '15px' }}>{admin.name}</span>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '2px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#93c5fd', fontSize: '12px' }}>@{admin.username}</span>
                        <span style={{ color: '#93c5fd', fontSize: '12px' }}>{admin.mobile}</span>
                        {admin.email && <span style={{ color: '#93c5fd', fontSize: '12px' }}>{admin.email}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {isSuperAdmin && (
                      <label style={{ background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                        Photo
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadProfilePic(admin.id, e.target.files[0])} />
                      </label>
                    )}
                    {isSuperAdmin && <button onClick={() => openEdit(admin)} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>}
                    {isSuperAdmin && admin.role !== 'superadmin' && (
                      <button onClick={() => toggleUser(admin.id, admin.is_active)} style={{ background: admin.is_active ? '#fee2e2' : '#d1fae5', color: admin.is_active ? '#dc2626' : '#065f46', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                        {admin.is_active ? 'Disable' : 'Enable'}
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '10px 20px', display: 'flex', gap: '24px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
                  {isSuperAdmin && admin.role === 'superadmin' && <span style={{ fontSize: '13px', color: '#374151' }}><strong>{admin.id}</strong> Super Admin ID</span>}
                  {workspaceAccounts.length > 0 && <span style={{ fontSize: '13px', color: '#374151' }}><strong>{workspaceAccounts.length}</strong> Admin / White Label</span>}
                  <span style={{ fontSize: '13px', color: '#374151' }}><strong>{employees.length}</strong> Employees</span>
                  <span style={{ fontSize: '13px', color: '#374151' }}><strong>{dealers.length}</strong> Dealers</span>
                  <span style={{ fontSize: '13px', color: '#374151' }}><strong>{customers.length}</strong> Customers</span>
                  <span style={{ fontSize: '13px', color: admin.is_active ? '#10b981' : '#dc2626', fontWeight: 'bold' }}>{admin.is_active ? 'Active' : 'Inactive'}</span>
                </div>

                {workspaceAccounts.length > 0 && (
                  <div>
                    <div style={{ background: '#eff6ff', padding: '8px 20px', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#1d4ed8' }}>Admin / White Label ({workspaceAccounts.length})</span>
                    </div>
                    {workspaceAccounts.map(account => {
                      const accountUsers = getAdminTreeUsers(account.id).filter(u => teamRoles.includes(u.role));
                      const accountEmployees = accountUsers.filter(u => u.role === 'employee');
                      const accountDealers = accountUsers.filter(u => u.role === 'dealer');
                      const accountCustomers = accountUsers.filter(u => u.role === 'customer');

                      return (
                        <div key={account.id} style={{ borderTop: '1px solid #e5e7eb', background: '#ffffff' }}>
                          <UserTable data={[account]} headerBg="#dbeafe" headerColor="#1d4ed8" showRole={true} showCreatedDate={false} />
                          <div style={{ background: '#f8fafc', padding: '8px 20px', display: 'flex', gap: '18px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '12px', color: '#475569' }}><strong>{accountEmployees.length}</strong> Employees</span>
                            <span style={{ fontSize: '12px', color: '#475569' }}><strong>{accountDealers.length}</strong> Dealers</span>
                            <span style={{ fontSize: '12px', color: '#475569' }}><strong>{accountCustomers.length}</strong> Customers</span>
                          </div>
                          {accountEmployees.length > 0 && <UserTable data={accountEmployees} headerBg="#d1fae5" headerColor="#065f46" showRole={false} showCreatedDate={false} />}
                          {accountDealers.length > 0 && <UserTable data={accountDealers} headerBg="#ede9fe" headerColor="#6d28d9" showRole={false} showCreatedDate={false} />}
                          {accountCustomers.length > 0 && <UserTable data={accountCustomers} headerBg="#e9f7d9" headerColor="#3f7d16" showRole={false} showCreatedDate={false} />}
                          {accountUsers.length === 0 && (
                            <div style={{ padding: '18px 20px', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>No team users under this account yet</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {!isSuperAdmin && employees.length > 0 && (
                  <div>
                    <div style={{ background: '#f0fdf4', padding: '8px 20px', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#065f46' }}>Employees ({employees.length})</span>
                    </div>
                    <UserTable data={employees} headerBg="#d1fae5" headerColor="#065f46" showRole={false} showCreatedDate={false} />
                  </div>
                )}

                {!isSuperAdmin && dealers.length > 0 && (
                  <div>
                    <div style={{ background: '#f5f3ff', padding: '8px 20px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#6d28d9' }}>Dealers ({dealers.length})</span>
                    </div>
                    <UserTable data={dealers} headerBg="#ede9fe" headerColor="#6d28d9" showRole={false} showCreatedDate={false} />
                  </div>
                )}

                {!isSuperAdmin && customers.length > 0 && (
                  <div>
                    <div style={{ background: '#f1fbdf', padding: '8px 20px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#3f7d16' }}>Customers ({customers.length})</span>
                    </div>
                    <UserTable data={customers} headerBg="#e9f7d9" headerColor="#3f7d16" showRole={false} showCreatedDate={false} />
                  </div>
                )}

                {adminUsers.length === 0 && (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No employees, dealers or customers under this admin yet</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {listTabKeys.includes(activeTab) && (
        <div id="user-filter-results" style={userTablePanelStyle}>
          <UserTable
            data={pagedListUsers}
            showRole={true}
            showCreatedBy={true}
            showCreatedDate={true}
            startIndex={userListStartIndex}
            emptyText="No users found in this tab"
            headerControls={(
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', minWidth: 0, maxWidth: '100%', flexWrap: 'nowrap' }}>
                <span style={{ color: 'white', fontSize: '12px', fontWeight: 800, whiteSpace: 'nowrap', minWidth: '58px', textAlign: 'center' }}>
                  {activeListUsers.length === 0 ? '0 reports' : `${userListStartIndex + 1}-${Math.min(userListStartIndex + userListPageSize, activeListUsers.length)} of ${activeListUsers.length}`}
                </span>
                <button
                  type="button"
                  onClick={() => setUserListPage((current) => Math.max(1, current - 1))}
                  disabled={safeUserListPage <= 1}
                  style={{ background: safeUserListPage <= 1 ? '#e0f2fe' : 'white', color: safeUserListPage <= 1 ? '#94a3b8' : '#1e3a5f', border: '1px solid #bfdbfe', width: '44px', minWidth: '44px', padding: '5px 0', borderRadius: '6px', cursor: safeUserListPage <= 1 ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: 800 }}
                >
                  Prev
                </button>
                <span style={{ color: 'white', fontSize: '11px', fontWeight: 800, whiteSpace: 'nowrap', minWidth: '40px', textAlign: 'center' }}>{safeUserListPage}/{userListPageCount}</span>
                <button
                  type="button"
                  onClick={() => setUserListPage((current) => Math.min(userListPageCount, current + 1))}
                  disabled={safeUserListPage >= userListPageCount}
                  style={{ background: safeUserListPage >= userListPageCount ? '#e0f2fe' : 'white', color: safeUserListPage >= userListPageCount ? '#94a3b8' : '#1e3a5f', border: '1px solid #bfdbfe', width: '46px', minWidth: '46px', padding: '5px 0', borderRadius: '6px', cursor: safeUserListPage >= userListPageCount ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: 800 }}
                >
                  Next
                </button>
              </span>
            )}
          />
        </div>
      )}

      {showReportsOption && reportsTab && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))', gap: '12px', marginTop: '20px', marginBottom: '20px' }}>
          {(() => {
            const tab = reportsTab;
            const isActive = activeTab === tab.key;
            const Icon = tab.Icon;
            return (
              <button key={tab.key} type="button" onClick={() => handleUserTabClick(tab.key)} style={{
                minHeight: '112px',
                padding: '14px',
                borderRadius: '12px',
                border: `1px solid ${isActive ? tab.tone : 'rgba(148, 163, 184, 0.35)'}`,
                cursor: 'pointer',
                background: isActive ? tab.gradient : `linear-gradient(135deg, #ffffff 0%, ${tab.soft} 100%)`,
                color: '#0f172a',
                boxShadow: isActive ? `0 16px 30px ${tab.shadow}` : '0 8px 18px rgba(15,23,42,0.07)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '14px',
                textAlign: 'left',
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease'
              }}>
                <span style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '4px', background: `linear-gradient(90deg, ${tab.tone}, ${tab.tone2})` }} />
                <span style={{ position: 'absolute', right: '-24px', bottom: '-26px', width: '74px', height: '74px', borderRadius: '50%', background: tab.tone2, opacity: isActive ? 0.16 : 0.1 }} />
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                  <span style={{ width: '40px', height: '40px', borderRadius: '11px', background: `linear-gradient(135deg, ${tab.tone}, ${tab.tone2})`, color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', boxShadow: `0 8px 18px ${tab.shadow}`, position: 'relative', zIndex: 1 }}>
                    <Icon size={20} strokeWidth={2.2} />
                  </span>
                  <span style={{ minWidth: '34px', padding: '4px 8px', borderRadius: '999px', background: isActive ? tab.tone : 'rgba(255,255,255,0.78)', color: isActive ? 'white' : tab.tone, border: `1px solid ${isActive ? tab.tone : 'rgba(255,255,255,0.92)'}`, fontSize: '11px', fontWeight: 'bold', textAlign: 'center', boxShadow: '0 4px 10px rgba(15,23,42,0.08)', position: 'relative', zIndex: 1 }}>
                    {tab.count}
                  </span>
                </span>
                <span style={{ display: 'block', fontSize: '14px', fontWeight: 800, lineHeight: 1.25, color: isActive ? tab.tone : '#0f172a', position: 'relative', zIndex: 1 }}>
                  {selectedUserTile ? `${tab.label} - ${selectedUserTile.name}` : tab.label}
                </span>
              </button>
            );
          })()}
        </div>
      )}

      {activeTab === 'reports' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minWidth: 0 }}>
            {activeReportPreview ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '18px' }}>{activeReportPreview.title}</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '12px' }}>{activeReportPreview.rows.length} rows shown | {activeReportPreview.generatedAt}</p>
                  </div>
                  <button type="button" onClick={downloadUserReport} style={{ background: '#2563eb', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                    Download CSV
                  </button>
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                  <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#1e3a5f', color: 'white' }}>
                        {activeReportPreview.columns.map(col => (
                          <th key={col.key} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px' }}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeReportPreview.rows.length === 0 ? (
                        <tr>
                          <td colSpan={activeReportPreview.columns.length} style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>No report data found</td>
                        </tr>
                      ) : activeReportPreview.rows.map((row, index) => (
                        <tr key={`${activeReportPreview.key}-${index}`} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                          {activeReportPreview.columns.map(col => (
                            <td key={col.key} style={{ padding: '10px 12px', fontSize: '13px', color: '#374151' }}>{row[col.key]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ minHeight: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '10px', fontSize: '14px' }}>
                Report Preview
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
